import dayjs, { Dayjs } from "dayjs";

export enum SalesPeriodPreset {
  ThisMonth = "month",
  Last7Days = "7d",
  Last30Days = "30d",
  Last90Days = "90d",
  Last12Months = "12m",
  ThisYear = "ytd",
  Custom = "custom",
}

export const SALES_PERIOD_PRESETS = Object.values(SalesPeriodPreset);

export const DEFAULT_SALES_PERIOD_PRESET = SalesPeriodPreset.ThisMonth;

export type SalesPeriod = { from: Dayjs; to: Dayjs };

export enum SalesComparison {
  PreviousPeriod = "previous",
  LastYear = "year",
}

export const SALES_COMPARISONS = Object.values(SalesComparison);

export enum SalesGranularity {
  Day = "day",
  Week = "week",
  Month = "month",
}

export const SALES_GRANULARITIES = Object.values(SalesGranularity);

export type SalesBucket = { start: Dayjs; end: Dayjs };

const LONGEST_PERIOD_SHOWN_BY_DAY = 35;
const LONGEST_PERIOD_SHOWN_BY_WEEK = 190;

function lastDays(today: Dayjs, days: number): SalesPeriod {
  return {
    from: today.subtract(days - 1, "day").startOf("day"),
    to: today.endOf("day"),
  };
}

function sinceStartOf(today: Dayjs, unit: "month" | "year"): SalesPeriod {
  return { from: today.startOf(unit), to: today.endOf("day") };
}

export function salesPeriodOf({
  preset,
  customFrom,
  customTo,
  today,
}: {
  preset: SalesPeriodPreset;
  customFrom?: string;
  customTo?: string;
  today: Dayjs;
}): SalesPeriod {
  switch (preset) {
    case SalesPeriodPreset.Last7Days:
      return lastDays(today, 7);
    case SalesPeriodPreset.Last30Days:
      return lastDays(today, 30);
    case SalesPeriodPreset.Last90Days:
      return lastDays(today, 90);
    case SalesPeriodPreset.Last12Months:
      return {
        from: today.subtract(12, "month").add(1, "day").startOf("day"),
        to: today.endOf("day"),
      };
    case SalesPeriodPreset.ThisYear:
      return sinceStartOf(today, "year");
    case SalesPeriodPreset.Custom: {
      const from = dayjs(customFrom);
      const to = dayjs(customTo);
      return from.isValid() && to.isValid() && !from.isAfter(to)
        ? { from: from.startOf("day"), to: to.endOf("day") }
        : sinceStartOf(today, "month");
    }
    default:
      return sinceStartOf(today, "month");
  }
}

export function daysIn(period: SalesPeriod): number {
  return period.to.startOf("day").diff(period.from.startOf("day"), "day") + 1;
}

const COMPARED_WITH_THE_SAME_DAYS_OF: Partial<
  Record<SalesPeriodPreset, "month" | "year">
> = {
  [SalesPeriodPreset.ThisMonth]: "month",
  [SalesPeriodPreset.ThisYear]: "year",
};

export function previousPeriodOf(
  period: SalesPeriod,
  preset: SalesPeriodPreset,
): SalesPeriod {
  const comparedUnit = COMPARED_WITH_THE_SAME_DAYS_OF[preset];
  if (comparedUnit)
    return {
      from: period.from.subtract(1, comparedUnit),
      to: period.to.subtract(1, comparedUnit),
    };
  return {
    from: period.from.subtract(daysIn(period), "day").startOf("day"),
    to: period.from.subtract(1, "day").endOf("day"),
  };
}

export function comparisonPeriodOf({
  period,
  preset,
  comparison,
}: {
  period: SalesPeriod;
  preset: SalesPeriodPreset;
  comparison: SalesComparison;
}): SalesPeriod {
  return comparison === SalesComparison.LastYear
    ? {
        from: period.from.subtract(1, "year"),
        to: period.to.subtract(1, "year"),
      }
    : previousPeriodOf(period, preset);
}

export function defaultGranularityOf(period: SalesPeriod): SalesGranularity {
  const days = daysIn(period);
  if (days <= LONGEST_PERIOD_SHOWN_BY_DAY) return SalesGranularity.Day;
  if (days <= LONGEST_PERIOD_SHOWN_BY_WEEK) return SalesGranularity.Week;
  return SalesGranularity.Month;
}

function bucketStartOf(date: Dayjs, granularity: SalesGranularity): Dayjs {
  if (granularity === SalesGranularity.Week)
    return date.subtract((date.day() + 6) % 7, "day").startOf("day");
  return date.startOf(granularity);
}

export function bucketsOf(
  period: SalesPeriod,
  granularity: SalesGranularity,
): SalesBucket[] {
  const buckets: SalesBucket[] = [];
  for (
    let start = bucketStartOf(period.from, granularity);
    !start.isAfter(period.to);
    start = start.add(1, granularity)
  ) {
    const nextStart = start.add(1, granularity);
    buckets.push({
      start: start.isBefore(period.from) ? period.from : start,
      end: nextStart.isAfter(period.to)
        ? period.to
        : nextStart.subtract(1, "millisecond"),
    });
  }
  return buckets;
}

export function isWithin(dateUtc: string, period: SalesPeriod): boolean {
  const date = dayjs(dateUtc);
  return !date.isBefore(period.from) && !date.isAfter(period.to);
}

export function bucketIndexOf(dateUtc: string, buckets: SalesBucket[]): number {
  return buckets.findIndex((bucket) =>
    isWithin(dateUtc, { from: bucket.start, to: bucket.end }),
  );
}
